import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, RotateCcw, Calendar, Clock, Gift, Users, ClipboardCheck, Wrench, Settings, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OnboardingWalkthrough from '../components/onboarding/OnboardingWalkthrough';
import api from '../utils/api';
import { useToast } from '../components/common/Toast';
import './Help.css';

/**
 * Help page -- role-filtered reference content.
 * Always accessible from the sidebar. Includes a "Show welcome guide" button
 * to re-trigger the onboarding walkthrough.
 */

// Help sections with role visibility
const HELP_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: HelpCircle,
    roles: ['staff', 'manager', 'admin'],
    items: [
      {
        q: 'How do I check my profile information?',
        a: 'Your profile is managed by your admin. If you notice anything incorrect (name, country, department, birthday), reach out to your admin to make changes.',
      },
      {
        q: 'What is my PTO year?',
        a: 'The PTO year runs from January 1 to December 31. Your PTO balance resets at the start of each calendar year. Bonus days carry over across years.',
      },
    ],
  },
  {
    id: 'time-off',
    title: 'Time Off',
    icon: Calendar,
    roles: ['staff', 'manager', 'admin'],
    items: [
      {
        q: 'How do I submit a time off request?',
        a: 'Go to "Time Off" in the sidebar, then click "Request Time Off." Select your dates, choose full day or half day (AM/PM), add any notes, and submit. Your manager will be notified and can approve or deny.',
      },
      {
        q: 'Can I request non-consecutive days?',
        a: 'Yes. When selecting dates, you can pick any combination of days within a single request. They don\'t have to be consecutive.',
      },
      {
        q: 'What happens if I don\'t have enough PTO balance?',
        a: 'You can still submit the request, but you\'ll be required to add a note explaining why. The request will be flagged for your manager so they know it exceeds your balance.',
      },
      {
        q: 'What is short notice?',
        a: 'Requests submitted within 3 business days of the first requested date are flagged as short notice. You can still submit them -- the flag just lets your manager know.',
      },
      {
        q: 'Can I cancel a request?',
        a: 'Yes. You can cancel any pending or approved request from the Time Off page. If cancelling an approved request, your PTO balance will be restored.',
      },
      {
        q: 'What if my request is denied?',
        a: 'Your manager must provide a reason when denying. You\'ll receive an email with the explanation. You can submit a new request with different dates.',
      },
    ],
  },
  {
    id: 'birthday',
    title: 'Birthday Day Off',
    icon: Gift,
    roles: ['staff', 'manager', 'admin'],
    items: [
      {
        q: 'How does the birthday day off work?',
        a: 'You get one extra day off during your birth month. It does not come from your PTO balance -- it\'s a separate benefit. You can use it any day during your birth month.',
      },
      {
        q: 'Do I need approval for my birthday day off?',
        a: 'Yes, it goes through the same approval process as a regular PTO request. Select "Birthday" as the category when submitting.',
      },
    ],
  },
  {
    id: 'holidays',
    title: 'Holidays & Calendar',
    icon: Clock,
    roles: ['staff', 'manager', 'admin'],
    items: [
      {
        q: 'How do I see my country\'s holidays?',
        a: 'Go to "Calendar" in the sidebar. Holidays observed for your country are shown on the calendar. They don\'t count against your PTO balance.',
      },
      {
        q: 'What if I request time off on a holiday?',
        a: 'The system will warn you that you\'re requesting a day that\'s already a holiday. You can still submit if you want, but holidays are already days off.',
      },
    ],
  },
  {
    id: 'w2-info',
    title: 'W2 (Full-Time) Employees',
    icon: Shield,
    roles: ['staff', 'manager', 'admin'],
    employmentType: 'staff_w2',
    items: [
      {
        q: 'Why don\'t I see a PTO balance?',
        a: 'As a W2 employee, your PTO is tracked externally through HR. FPS OS is used to log your time off for team visibility -- your entries are recorded automatically without manager approval.',
      },
      {
        q: 'Do I still get a birthday day off?',
        a: 'Yes. The birthday day off applies to all employees regardless of employment type. Submit it the same way.',
      },
    ],
  },
  {
    id: 'approvals',
    title: 'Approvals (Managers)',
    icon: ClipboardCheck,
    roles: ['manager', 'admin'],
    items: [
      {
        q: 'How do I approve or deny requests?',
        a: 'Go to "Approvals" in the sidebar. You\'ll see pending requests from your department. Click the checkmark to approve or the X to deny (a reason is required for denials).',
      },
      {
        q: 'Can I approve multiple requests at once?',
        a: 'Yes. Check the boxes next to multiple requests, then click "Approve Selected" to batch approve them.',
      },
      {
        q: 'What if I forget about a pending request?',
        a: 'You\'ll receive an email reminder if a request goes unaddressed for more than 2 business days.',
      },
      {
        q: 'Can I remove an already-approved request?',
        a: 'Yes. In the "Recently Approved" section of the Approvals page, you can retroactively remove a request with a reason. The employee\'s PTO balance will be restored.',
      },
    ],
  },
  {
    id: 'tools',
    title: 'Apps & Tools',
    icon: Wrench,
    roles: ['manager', 'admin'],
    items: [
      {
        q: 'How do I add a tool to the directory?',
        a: 'Go to "Manage Tools" in the sidebar. Click "Add Tool," fill in the details, and the system can auto-fill descriptions using AI. Check existing categories before creating a new one.',
      },
      {
        q: 'What does "Featured" mean?',
        a: 'Featured tools appear on the dashboard for quick access. Only admins can toggle which tools are featured.',
      },
    ],
  },
  {
    id: 'admin',
    title: 'Administration',
    icon: Settings,
    roles: ['admin'],
    items: [
      {
        q: 'How do I set up a new employee?',
        a: 'In Admin > Users, the employee appears after their first Google login (in "Pending Setup" status). Set their country, department, birthday, and employment type, then activate them.',
      },
      {
        q: 'How do I set PTO allocations?',
        a: 'Go to Admin > Allocations. Select the current PTO year, then set each contractor\'s annual days. The system suggests prorated amounts for mid-year hires.',
      },
      {
        q: 'How do holiday calendars work?',
        a: 'Each country has a set of national holidays. In Admin > Holidays, you select which ones FPS observes (up to 9 per country). Each employee follows their assigned country\'s calendar.',
      },
      {
        q: 'What\'s the difference between W2 and Remote employees?',
        a: 'W2 (full-time) employees have PTO tracked externally -- their requests auto-approve and don\'t draw from a balance. Remote (contractor) employees use the internal PTO tracker with allocations you set.',
      },
      {
        q: 'How do I award bonus days?',
        a: 'In Admin > Allocations, select a user and use the "Award Bonus" section. Bonus days carry over across years and are drawn after regular PTO is exhausted.',
      },
    ],
  },
];

function Help() {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const [expandedSections, setExpandedSections] = useState(new Set(['getting-started', 'time-off']));
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleShowWalkthrough = async () => {
    // Reset onboarding flag so the walkthrough shows
    try {
      await api.post('/users/me/onboarding/reset');
    } catch {
      // Show it locally even if reset fails
    }
    setShowWalkthrough(true);
  };

  const handleWalkthroughDismiss = () => {
    setShowWalkthrough(false);
    if (refreshUser) refreshUser();
  };

  // Filter sections by user role and employment type
  const visibleSections = HELP_SECTIONS.filter(section => {
    if (!section.roles.includes(user?.role)) return false;
    if (section.employmentType && section.employmentType !== user?.employment_type) return false;
    return true;
  });

  return (
    <div className="help-page">
      <div className="page-header">
        <HelpCircle size={24} strokeWidth={2} />
        <h1>Help & Getting Started</h1>
      </div>

      {/* Welcome guide re-trigger */}
      <div className="help-welcome-card">
        <div className="help-welcome-content">
          <h3>Welcome Guide</h3>
          <p>View the getting started walkthrough to see key features for your role.</p>
        </div>
        <button className="btn-outline help-welcome-btn" onClick={handleShowWalkthrough}>
          <RotateCcw size={14} />
          Show Welcome Guide
        </button>
      </div>

      {/* Help sections */}
      {visibleSections.map(section => {
        const isExpanded = expandedSections.has(section.id);
        const SectionIcon = section.icon;

        return (
          <div key={section.id} className="help-section">
            <button className="help-section-header" onClick={() => toggleSection(section.id)}>
              <div className="help-section-title">
                <SectionIcon size={18} strokeWidth={2} />
                <span>{section.title}</span>
                <span className="help-section-count">{section.items.length}</span>
              </div>
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            {isExpanded && (
              <div className="help-section-content">
                {section.items.map((item, i) => (
                  <div key={i} className="help-item">
                    <div className="help-question">{item.q}</div>
                    <div className="help-answer">{item.a}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Walkthrough modal */}
      {showWalkthrough && user && (
        <OnboardingWalkthrough user={user} onDismiss={handleWalkthroughDismiss} />
      )}
    </div>
  );
}

export default Help;
